import {
  faChevronDown,
  faChevronRight,
  IconDefinition,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSet } from '@hooks/useSet'
import {
  FC,
  CSSProperties,
  ReactNode,
  forwardRef,
  RefObject,
  Fragment,
  useId,
} from 'react'
import { twMerge } from '@lib/tailwind-merge'
import { Tooltip } from '@lib/tooltip'
import { Spinner } from 'components/loaders'
import { twJoin } from 'tailwind-merge'
import { Link } from 'react-router-dom'

type Content = { content: ReactNode } | { icon?: IconDefinition; label: string }

interface ItemAnchor {
  as: 'a'
  disabled?: boolean
  href: string
  target?: string
}

interface ItemButton {
  as?: 'button'
  disabled?: boolean
  onClick: () => void
  preventEventPropagation?: boolean
}

interface ItemDiv {
  as: 'div'
}

interface ItemLink {
  as: 'link'
  disabled?: boolean
  to: string
  target?: string
}

type ItemWithoutContent = {
  className?: string
  tooltip?: ReactNode
  tooltipProps?: Omit<React.ComponentProps<typeof Tooltip>, 'id'>
  loading?: boolean
} & (ItemAnchor | ItemButton | ItemDiv | ItemLink)

export type Item = ItemWithoutContent & Content

type ParentItem = {
  disabled?: boolean
  children: Item[]
} & Content

export interface DropdownMenuProps {
  className?: string
  hidden: boolean
  items: (ParentItem | Item | undefined | boolean)[]
  onItemClick?: () => void
  onMouseLeave?: () => void
  preventEventPropagation?: boolean
  ref?: RefObject<HTMLDivElement>
  style?: CSSProperties
  header?: string
  beforeItems?: ReactNode
  itemTag?: Item['as']
}

function isParentItem(item: ParentItem | Item): item is ParentItem {
  return 'children' in item
}

const WithTooltip: FC<
  Pick<Item, 'tooltip' | 'tooltipProps'> & {
    children: (id: string) => ReactNode
  }
> = ({ children, tooltip, tooltipProps }) => {
  const id = useId()

  const tooltipPropsWithDefaults: Omit<
    React.ComponentProps<typeof Tooltip>,
    'id'
  > = {
    delayShow: 100,
    place: 'top',
    ...tooltipProps,
  }

  return (
    <>
      {children(id)}
      <Tooltip id={id} {...tooltipPropsWithDefaults}>
        {tooltip}
      </Tooltip>
    </>
  )
}

const Item: FC<ItemWithoutContent & { children: ReactNode }> = ({
  children,
  className,
  tooltip,
  tooltipProps,
  ...props
}) => {
  const defaultClassName = twMerge(
    'flex flex-row items-center',
    'text-sm text-neutral-900 whitespace-nowrap font-normal',
    'border-b border-neutral-200 last:border-none',
    'group/menu-item',
    'disabled' in props &&
      props.disabled &&
      'text-neutral-400 cursor-not-allowed bg-neutral-200 hover:bg-neutral-200',
    className,
  )

  if (props.as === 'a')
    return (
      <WithTooltip tooltip={tooltip} tooltipProps={tooltipProps}>
        {(id) => (
          <a
            data-tooltip-id={id}
            className={twJoin(
              defaultClassName,
              'hover:bg-neutral-100 px-6 h-11',
            )}
            href={props.disabled ? undefined : props.href}
            target={props.target}
          >
            {children}
          </a>
        )}
      </WithTooltip>
    )

  if (props.as === 'link')
    return (
      <WithTooltip tooltip={tooltip} tooltipProps={tooltipProps}>
        {(id) => (
          <Link
            data-tooltip-id={id}
            className={twJoin(
              defaultClassName,
              'hover:bg-neutral-100 px-6 h-11',
            )}
            to={props.disabled ? '' : props.to}
            target={props.target}
          >
            {children}
          </Link>
        )}
      </WithTooltip>
    )

  if (props.as === 'div')
    return (
      <WithTooltip tooltip={tooltip} tooltipProps={tooltipProps}>
        {(id) => (
          <div data-tooltip-id={id} className={defaultClassName}>
            {children}
          </div>
        )}
      </WithTooltip>
    )

  return (
    <WithTooltip tooltip={tooltip} tooltipProps={tooltipProps}>
      {(id) => (
        <button
          data-tooltip-id={id}
          className={twJoin(defaultClassName, 'hover:bg-neutral-100 px-6 h-11')}
          onClick={props.disabled ? (e) => e.preventDefault() : props.onClick}
          onMouseDown={(event) =>
            props.preventEventPropagation && event.stopPropagation()
          }
          onTouchStart={(event) =>
            props.preventEventPropagation && event.stopPropagation()
          }
        >
          {children}
        </button>
      )}
    </WithTooltip>
  )
}

export const DropdownMenu = forwardRef<HTMLDivElement, DropdownMenuProps>(
  (
    {
      hidden,
      beforeItems,
      items,
      onItemClick = () => undefined,
      onMouseLeave,
      preventEventPropagation = false,
      className,
      style,
      header,
    },
    ref,
  ) => {
    const {
      set: expandedItems,
      add: expandItem,
      remove: collapseItem,
    } = useSet<number>()

    if (hidden) return null

    return (
      <div
        className={twMerge(
          'absolute right-0 z-10 flex flex-col bg-white border rounded-lg drop-shadow-lg border-neutral-300 min-w-[140px]',
          className,
        )}
        onMouseLeave={onMouseLeave}
        style={style}
        ref={ref}
      >
        {header && (
          <div className="px-6 py-3 text-xs font-medium uppercase border-b bg-neutral-100 text-neutral-500 border-neutral-200">
            {header}
          </div>
        )}
        {beforeItems}
        {items
          .reduce<(ParentItem | Item)[]>((acc, item) => {
            if (!item) return acc
            if (item === true) return acc

            return [...acc, item]
          }, [])
          .map((item, index) => {
            const isExpanded = isParentItem(item) && expandedItems.has(index)

            const handleItemClick = (item: Item) => {
              if (item.as === 'button') item.onClick()
              onItemClick()
            }

            const handleRootItemClick = () => {
              if (isParentItem(item)) {
                const operation = isExpanded ? collapseItem : expandItem
                operation(index)
              } else {
                handleItemClick(item)
              }
            }

            const renderContent = (item: Content) => {
              if ('content' in item) return item.content

              return (
                <>
                  {item.icon && (
                    <FontAwesomeIcon
                      icon={item.icon}
                      className="mr-2 text-neutral-600 group-disabled/menu-item:text-neutral-400"
                      fixedWidth
                    />
                  )}
                  {item.label}
                </>
              )
            }

            return (
              <Fragment key={`menu-item-key-${index}`}>
                {isParentItem(item) ? (
                  <Item
                    as="button"
                    key={`menu-item-${index}`}
                    onClick={handleRootItemClick}
                    preventEventPropagation={preventEventPropagation}
                  >
                    {renderContent(item)}
                    {isParentItem(item) && (
                      <FontAwesomeIcon
                        className="pl-3 ml-auto text-neutral-600"
                        icon={isExpanded ? faChevronDown : faChevronRight}
                      />
                    )}
                  </Item>
                ) : (
                  <Item {...item} key={`menu-item-${index}`}>
                    {item.loading ? (
                      <Spinner className="mx-auto" />
                    ) : (
                      renderContent(item)
                    )}
                  </Item>
                )}
                {isParentItem(item) &&
                  expandedItems.has(index) &&
                  item.children.map((childItem, index) => (
                    <Item
                      key={`menu-child-item-${index}`}
                      className="bg-neutral-50"
                      {...childItem}
                    >
                      {childItem.loading ? (
                        <Spinner className="mx-auto" />
                      ) : (
                        renderContent(childItem)
                      )}
                    </Item>
                  ))}
              </Fragment>
            )
          })}
      </div>
    )
  },
)

DropdownMenu.displayName = 'DropdownMenu'
